import { useState } from "react";
import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import {
  Check,
  X,
  Plus,
  Pencil,
  Trash2,
  RemoveFormatting,
  View,
} from "lucide-react";
import {
  RuleVersion,
} from "../../shared/schema";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AlertDialog } from "@/widgets/AlertDailog";
import { PaginationControl } from "@/widgets/renderPagination";
import { useLocation } from "wouter";
import { Popup } from "@/widgets/RuleDetailDailog";
import { FilterTypeSearch } from "@/widgets/searchFilterType";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { CreateFormDialog } from "../VersionRule/CreateFormDailog";
import { useDeletePortalForm, useFilterForms, usePartnerData, usePortalForms, usetoggglePublishForm } from "@/hooks/useLocalStorage";
import { PortalForm } from "@/lib/api-services";
import { DeleteConfirmationDialog } from "@/widgets/DeleteModal";
import { parseApiErrorMessage } from "@/hooks/parseApiErrorMessage";
import { Input } from "../ui/input";
import { ConfirmationDialog } from "@/widgets/AlertCommon";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

interface TableHierarchicalNodeProps {
  topLevelData: any[];
  pageIndex: number;
  pageSize: number;
  totalCount: number;
  fromType: string
  onPageChange: (code: number) => void;
  onEditForm: (data: any) => void;
}

function TableForm({
  topLevelData,
  pageIndex,
  pageSize,
  totalCount,
  fromType,
  onPageChange,
  onEditForm,
}: TableHierarchicalNodeProps) {
  const [currentData, setCurrentData] = useState<PortalForm[]>([]);
  const [alertOpen, setAlertOpen] = React.useState(false);
  const [alertMessage, setAlertMessage] = React.useState("");
  const [reqStatus, setReqStatus] = React.useState(false);
  const [showPopup, setShowPopup] = React.useState(false);
  const [dataHead, setDataHead] = React.useState<RuleVersion | undefined>();
  const [dataContent, setDataContent] = React.useState<string | null>("");
  const [location, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const [popupName, setPopupName] = React.useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<PortalForm | null>(null);
  const [itemToPublish, setItemToPublish] = useState<PortalForm | null>(null);

  const deletePortalForm = useDeletePortalForm();
  const togglePublishMutation = usetoggglePublishForm();


  React.useEffect(() => {
    if (topLevelData) {
      setCurrentData(topLevelData);
    }
  }, [topLevelData]);

  function handleDeleteClick(e: React.MouseEvent, item: PortalForm) {
    e.stopPropagation();
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  }

  function handleConfirmDelete(authCode?: string) {
    if (!itemToDelete) return;

    deletePortalForm.mutate({
      Id: itemToDelete.FormId,
      formType: fromType,
      authCode: authCode
    });
  }

  function handlePublish(e: React.MouseEvent, item: PortalForm) {
    e.stopPropagation();
    setItemToPublish(item);
    setApproveDialogOpen(true);
  }

  function handleConfirmPublish() {
    if (!itemToPublish) return;
    togglePublishMutation.mutate({ id: itemToPublish?.Id, formType: fromType });
  }

  React.useEffect(() => {
    if (deletePortalForm.isSuccess === true) {
      setDeleteDialogOpen(false);
      setReqStatus(deletePortalForm.isSuccess);
      setAlertMessage(`Portal Form deleted Successfully!`);
      setItemToDelete(null);
      setAlertOpen(true);
      deletePortalForm.reset();
    } else if (deletePortalForm.isError) {
      const raw = deletePortalForm.error as Error;
      const parsedMsg = parseApiErrorMessage(raw.message);
      setReqStatus(deletePortalForm.isSuccess);
      setDeleteDialogOpen(false);
      setAlertMessage(parsedMsg);
      setAlertOpen(true);
      deletePortalForm.reset();
    }
  }, [deletePortalForm]);

  React.useEffect(() => {
    if (togglePublishMutation.isSuccess === true) {
      setApproveDialogOpen(false);
      setReqStatus(togglePublishMutation.isSuccess);
      setAlertMessage(`${itemToPublish?.Published === true ? "Form UnPublished Successfully!" : "Form Published Successfully!"}`);
      setItemToPublish(null);
      setAlertOpen(true);
      togglePublishMutation.reset();
    } else if (togglePublishMutation.isError) {
      const raw = togglePublishMutation.error as Error;
      const parsedMsg = parseApiErrorMessage(raw.message);
      setReqStatus(togglePublishMutation.isSuccess);
      setApproveDialogOpen(false);
      setAlertMessage(parsedMsg);
      setAlertOpen(true);
      togglePublishMutation.reset();
    }
  }, [togglePublishMutation]);

  return (
    <div>
      <div className="border bg-white border-gray-200 rounded-lg p-2">
        <Table>
          <TableHeader>
            <TableRow>
              <>
                <TableHead>Form ID</TableHead>
                <TableHead>Form Code</TableHead>
                <TableHead>Form Name</TableHead>
                {/* <TableHead>Form Type</TableHead> */}
                <TableHead>Partner Type</TableHead>
                <TableHead>Build Form</TableHead>
                <TableHead>Version No.</TableHead>
                <TableHead>Published</TableHead>
                <TableHead>Actions</TableHead>
                <TableHead>Version</TableHead>
              </>
            </TableRow>
          </TableHeader>
          <TableBody>
            {currentData?.map((data) => (
              <TableRow key={data.Id}>
                <TableCell className="font-mono">{data.FormId}</TableCell>
                <TableCell className="font-mono">{data.FormCode}</TableCell>

                <TableCell className="max-w-xs truncate">
                  {data.FormName}
                </TableCell>

                <TableCell className="max-w-xs truncate">
                  {data.FormPartnerName}
                </TableCell>

                <TableCell className="max-w-xs truncate">
                  <Button
                    variant={"link"}
                    onClick={() => {
                      setPopupName("Raw with Value")
                    }}
                  >
                    <RemoveFormatting />
                  </Button>
                </TableCell>

                <TableCell className="max-w-xs truncate">
                  {data.Version}
                </TableCell>

                <TableCell className="max-w-xs truncate">
                  {data.Published === true ? (
                    <Button variant={"link"}>
                      <Check className="h-4 w-4 mr-1 text-green-800" />
                    </Button>
                  ) : (
                    <Button variant={"link"}>
                      <X className="h-4 w-4 mr-1 text-red-800" />
                    </Button>
                  )}
                </TableCell>

                <TableCell>
                  <div className="flex flex-row items-center space-x-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="min-w-6 h-6 p-0"
                          tooltip="More actions"
                        >
                          <Plus className="h-3 w-3" />
                          <span className="sr-only">More actions</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        className="min-w-[180px]"
                      >
                        <DropdownMenuItem
                          onClick={(e) => handlePublish(e, data)}
                          className="flex items-center px-1 cursor-pointer"
                        >
                          {data.Published !== true ? (
                            <Check className="h-4 w-4 mr-1 text-green-600" />
                          ) : (
                            <X className="h-4 w-4 mr-1 text-red-600" />
                          )}
                          <span className="text-sm">
                            {data.Published !== true ? "Publish" : "Unpublish"}
                          </span>
                        </DropdownMenuItem>

                        <DropdownMenuItem
                          className="flex items-center px-1 cursor-pointer"
                          onClick={() => onEditForm(data)}
                        >
                          <Pencil className="h-4 w-4 mr-1" />
                          <span className="text-sm">Edit Form</span>
                        </DropdownMenuItem>

                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            setLocation(
                              `/rules/edit/${data.Id}?name=${data.FormName}`
                            );
                          }}
                          className="flex items-center px-1 cursor-pointer"
                        >
                          <View />
                          <span className="text-sm">View Form</span>
                        </DropdownMenuItem>

                        <DropdownMenuItem
                          onClick={(e) => handleDeleteClick(e, data)}
                          className="flex items-center px-1 cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          <span className="text-sm">
                            Delete
                          </span>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </TableCell>

                <TableCell className="max-w-xs truncate">
                  <Button
                    variant={"link"}
                    onClick={() => {
                      setLocation(
                        `/ruleversion/${data.FormId}/${fromType}`
                      );
                    }}
                  >
                    Version History
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <div className="mt-2">
          <PaginationControl
            pageIndex={pageIndex}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={onPageChange}
          />
        </div>
      </div>

      <AlertDialog
        open={alertOpen}
        onOpenChange={setAlertOpen}
        message={alertMessage}
        status={reqStatus}
      />

      {showPopup && (
        <Popup
          popupName={popupName}
          heading={dataHead}
          content={dataContent}
          onClose={() => setShowPopup(false)}
        />
      )}

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleConfirmDelete}
        isPending={deletePortalForm.isPending}
        itemName={(itemToDelete as PortalForm)}
        itemType={"Portal Forms"}
        requireAuthCode={itemToDelete?.IsActive === true ? true : false}
      // requireAuthCode={itemToDelete?.IsActive === true ? false : true}
      />

      <ConfirmationDialog
        open={approveDialogOpen}
        onOpenChange={setApproveDialogOpen}
        onConfirm={handleConfirmPublish}
        isPending={togglePublishMutation.isPending}
        heading={itemToPublish?.Published === true ? "Confirm UnPublished Request" : "Confirm Published Request"}
        message={`Are you sure you want to ${itemToPublish?.Published === true ? "UnPublished" : "Published"} this Form?`}
        confirmLabel={`${itemToPublish?.Published === true ? "UnPublish" : "Publish"}`}
      // cancelLabel="Cancel"
      />
    </div>
  );
}

type PortalFormsProps = {
  fromType: string;
};

export function PortalForms({ fromType }: PortalFormsProps) {
  console.log("fromType", fromType)
  const [topLevelTypes, setTopLevelTypes] = useState<PortalForm[]>([]);

  const [typeInput, setTypeInput] = useState<string>("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterTypeData, setFilterTypeData] = useState<PortalForm[]>([]);
  const [filterName, setFilterName] = useState<string>("");

  const [formNameInput, setFormNameInput] = useState<string>("");
  const [formCodeInput, setFormCodeInput] = useState<number | undefined>();

  const [formName, setFormName] = useState<string | undefined>();
  const [partnerTypeInput, setpartnerTypeInput] = useState<string | undefined>("");
  const [partnerType, setPartnerType] = useState<string | undefined>("");
  const [formCode, setFormCode] = useState<number | undefined>();
  const [formType, setFormType] = useState<string>(fromType);


  // Form dialog states - controlled from parent
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [editFormData, setEditFormData] = useState<any>(null);

  // pagination states
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(10);

  const { data: types, isLoading } = usePortalForms(
    pageIndex,
    pageSize,
    formType,
    formCode,
    formName,
    partnerType
  );

  const { data: partners } = usePartnerData(
    1,
    undefined,
  );
  console.log("partners", partners)

  React.useEffect(() => {
    setFormType(fromType);
    setPageIndex(1);
  }, [fromType]);


  React.useEffect(() => {
    setTopLevelTypes(types?.items ?? []);
    setTotalCount(types?.meta?.total ?? 0);
  }, [types]);

  const { data: filterTypes, isFetching: isFilterFetching } = useFilterForms(
    filterName.length >= 3 ? filterName : "",
    pageSize,
    fromType
  );

  React.useEffect(() => {
    if (filterTypes && filterTypes.length > 0) {
      setFilterTypeData(filterTypes)
    } else {
      setFilterTypeData([])
    }
  }, [filterTypes])

  React.useEffect(() => {
    const selectedItemStr = localStorage.getItem("cm:selectedItem");
    if (selectedItemStr) {
      try {
        const selectedItem = JSON.parse(selectedItemStr);
        console.log("Navigate to item in TreeExplorer:", selectedItem);
        localStorage.removeItem("cm:selectedItem");
      } catch (error) {
        console.error("Error parsing selected item:", error);
        localStorage.removeItem("cm:selectedItem");
      }
    }
  }, []);

  const handleSearch = () => {
    setFormName(typeInput || undefined);
    setFormCode(formCodeInput);
    setPartnerType(partnerTypeInput);
    setPageIndex(1);
  };

  const handleRefresh = (close: boolean) => {
    setFormNameInput("");
    setPartnerType("");
    setFormCodeInput(undefined);

    setFormName(undefined);
    setFormCode(undefined);
    setpartnerTypeInput("")

    setPageIndex(1);
    if (close) setFilterOpen(false);
  };

  const handleAddForm = () => {
    setEditFormData(null);
    setOpenCreateDialog(true);
  };

  const handleEditForm = (data: any) => {
    setEditFormData(data);
    setOpenCreateDialog(true);
  };

  return (
    <div className="bg-gray-50 min-h-full">
      <div className="mb-4 md:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
          <div className="min-w-0">
            <h2 className="text-lg md:text-xl font-semibold text-gray-900">
              {formType === "PortalForm" ? "Public Facing" : formType === "WorkflowForm" ? "Workbench" : formType === "PartnerForm" ? "" : ""} {formType}s
            </h2>
            <p className="text-gray-600 mt-1 text-sm md:text-base">
              Manage and create {formType}s
            </p>
          </div>

          <div className="flex items-center space-x-2 md:space-x-3 flex-shrink-0">
            <Button
              className="bg-workfinity-500 hover:bg-workfinity-600 text-sm md:text-base"
              size="sm"
              onClick={() => setFilterOpen(true)}
            >
              Filter Form
            </Button>
            <Button
              className="bg-workfinity-500 hover:bg-workfinity-600 text-sm md:text-base"
              size="sm"
              onClick={handleAddForm}
            >
              <Plus className="h-3 w-3 md:h-4 md:w-4 mr-2" />
              Add Form
            </Button>
          </div>
        </div>
      </div>
      <div>
        {filterOpen &&
          <Card className="mb-4 md:mb-6">
            <CardContent className="pt-0">
              <div className="flex flex-col md:flex-row md:items-center md:space-x-4 space-y-3 md:space-y-0">
                <div className="w-[450px]">
                  <FilterTypeSearch
                    filterTypeData={filterTypeData}
                    isLoading={isLoading}
                    value={typeInput}
                    onChange={setTypeInput}
                    onSearch={(query) => setFilterName(query)}
                  />
                </div>
                <div className="w-[450px]">
                  <Input
                    type="number"
                    placeholder="Form ID"
                    value={formCodeInput ?? ""}
                    onChange={(e) =>
                      setFormCodeInput(
                        e.target.value ? Number(e.target.value) : undefined
                      )
                    }
                  />
                </div>
                <div className="w-[450px]">
                  <Select
                    value={partnerTypeInput}
                    onValueChange={(value) => setpartnerTypeInput(value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Partner" />
                    </SelectTrigger>

                    <SelectContent>
                      {partners?.data?.items?.map((partner: any) => (
                        <SelectItem
                          key={partner.ID}
                          value={partner.ID.toString()}
                        >
                          {partner.PartnerName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="mt-4 flex justify-center space-x-2">
                <Button onClick={handleSearch}>Search</Button>
                <Button variant="outline" onClick={() => handleRefresh(false)}>
                  Refresh
                </Button>
                <Button variant="outline" onClick={() => handleRefresh(true)}>
                  Close
                </Button>
              </div>
            </CardContent>
          </Card>
        }
      </div>

      {!isLoading ? (
        <Card>
          <CardContent className="p-4 md:p-6">
            <div className="space-y-2">
              {topLevelTypes && topLevelTypes.length > 0 ? (
                <>
                  <TableForm
                    topLevelData={topLevelTypes}
                    pageIndex={pageIndex}
                    pageSize={pageSize}
                    totalCount={totalCount}
                    fromType={fromType}
                    onPageChange={setPageIndex}
                    onEditForm={handleEditForm}
                  />
                </>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No Form found. Create to get started.</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="p-6">Loading...</div>
      )}

      <CreateFormDialog
        open={openCreateDialog}
        onOpenChange={setOpenCreateDialog}
        initialData={editFormData}
        fromType={fromType}
      />
    </div>
  );
}